import React, { useState, useEffect } from 'react';
import { blogAPI } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import RichTextEditor from '../Shared/RichTextEditor';
import '../Shared/FormStyles.scss';

const BlogManager = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image: '',
    category: '',
    author: '',
    isPublished: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await blogAPI.getAllAdmin();
      setPosts(response.data);
    } catch (err) {
      setError('Failed to fetch blog posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await blogAPI.update(editing._id, formData);
        setSuccess('Blog post updated successfully');
      } else {
        await blogAPI.create(formData);
        setSuccess('Blog post created successfully');
      }
      resetForm();
      fetchPosts();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save blog post');
    }
  };

  const handleEdit = (post) => {
    setEditing(post);
    setFormData({
      title: post.title || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      image: post.image || '',
      category: post.category || '',
      author: post.author || '',
      isPublished: post.isPublished || false,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await blogAPI.delete(id);
      setSuccess('Blog post deleted successfully');
      fetchPosts();
    } catch (err) {
      setError('Failed to delete blog post');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      image: '',
      category: '',
      author: '',
      isPublished: false,
    });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="blog-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Blog Post' : 'Add New Blog Post'}</h2>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <ImageUploader
            value={formData.image}
            onChange={(url) => setFormData({ ...formData, image: url })}
            label="Featured Image"
          />
          <div className="form-group">
            <label>Excerpt</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Content</label>
            <RichTextEditor
              value={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>Author</label>
            <input
              type="text"
              value={formData.author}
              onChange={(e) => setFormData({ ...formData, author: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isPublished}
                onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
              />
              Published
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Blog Posts</h2>
        {posts.length === 0 ? (
          <div className="empty-state">No blog posts found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Author</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post._id}>
                  <td>{post.title}</td>
                  <td>{post.category}</td>
                  <td>{post.author}</td>
                  <td>{post.isPublished ? 'Published' : 'Draft'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(post)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(post._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default BlogManager;
